import * as Fp from "../Fp/index.js";
import { infinity } from "./infinity.js";
import { isZero } from "./isZero.js";

/**
 * Double a G1 point
 *
 * @see https://voltaire.tevm.sh/crypto for BN254 cryptography documentation
 * @since 0.0.0
 * @param {import('../G1PointType.js').G1PointType} point - Point to double
 * @returns {import('../G1PointType.js').G1PointType} Doubled point
 * @throws {never}
 * @example
 * ```javascript
 * import * as G1 from './crypto/bn254/G1/index.js';
 * const point = G1.generator();
 * const doubled = G1.double(point);
 * ```
 */
export function double(point) {
	if (isZero(point)) return infinity();

	const x2 = Fp.mul(point.x, point.x);
	const y2 = Fp.mul(point.y, point.y);
	const y4 = Fp.mul(y2, y2);

	const s = Fp.mul(Fp.mul(point.x, y2), 4n);
	const m = Fp.mul(x2, 3n);

	const m2 = Fp.mul(m, m);
	const two_s = Fp.mul(s, 2n);
	const x_result = Fp.sub(m2, two_s);

	const s_minus_x = Fp.sub(s, x_result);
	const eight_y4 = Fp.mul(y4, 8n);
	const y_result = Fp.sub(Fp.mul(m, s_minus_x), eight_y4);

	const z_result = Fp.mul(Fp.mul(point.y, point.z), 2n);

	return /** @type {import('../G1PointType.js').G1PointType} */ ({
		x: x_result,
		y: y_result,
		z: z_result,
	});
}
